import React, { useState, useEffect, useRef } from 'react';
import { PostRequest, GetRequest } from 'components/common/index.js';
import { ContentPopup } from 'components/ContentPopup.jsx';
import AttributePropertyTags from 'components/quote_builder/attributePropertyTags.jsx';
import styles from 'components/quote_builder_widget/settings/_style.module.scss';

const translate = (key) => t(`quote_builder_widget.${key}`);

// The printing machines the widget prices printing on. The owner adds a machine as they add a material -- materials
// first: only the machines one of the widget's materials can be printed on are offered. Each machine is shown as the
// Manufacturing Cost Calculator settings list shows it (SettingsUserUnitPriceAttributeList): its name and the same
// property pills, read through the printing machine settings configuration.
const PrintingMachinesSection = ({ onPrintingMachinesChange }) => {
  const [machines, setMachines] = useState([]);
  const [widgetMaterials, setWidgetMaterials] = useState(false);
  const [settingsConfiguration, setSettingsConfiguration] = useState(null);
  const [machineFormConfiguration, setMachineFormConfiguration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [updatingId, setUpdatingId] = useState(null);
  const [showAddPopup, setShowAddPopup] = useState(false);
  const addPopupRef = useRef();

  const fetchMachines = () => {
    GetRequest({
      url: '/quote-builder/widgets/printing-machines',
      callback: (data) => {
        setMachines(data.printing_machines);
        setWidgetMaterials(data.widget_materials);
        setLoading(false);
      },
      // Any failure -- an error status, an expired session, a dropped connection -- ends the loading state.
      onError: () => {
        setLoading(false);
        setErrorMessage(translate('failed_to_load_printing_machines'));
      }
    });
  };

  const fetchConfigurations = () => {
    GetRequest({
      url: '/quote-builder/config/settings/user_unit_prices/printing_machine',
      useCache: true,
      callback: (data) => { setSettingsConfiguration(data); }
    });
    GetRequest({
      url: '/quote-builder/user_unit_price_attributes/config/printing_machine/printing_machine',
      callback: (data) => { setMachineFormConfiguration(data); }
    });
  };

  useEffect(() => {
    fetchMachines();
    fetchConfigurations();
  }, []);

  const tabConfiguration = (settingsConfiguration?.tabs || []).find((tab) => tab.form_type === 'UserUnitPriceAttribute');

  const closeAddPopup = () => {
    setShowAddPopup(false);
    if (addPopupRef.current) {
      addPopupRef.current.style.display = 'none';
    }
  };

  const updateWidgetStatus = (machine, widgetEnabled) => {
    setUpdatingId(machine.id);
    setErrorMessage('');
    PostRequest({
      url: `/quote-builder/user_unit_price_attributes/${machine.id}/update-widget-status`,
      data: { widget_enabled: widgetEnabled ? 'true' : 'false' },
      callback: () => {
        setUpdatingId(null);
        closeAddPopup();
        fetchMachines();
        if (onPrintingMachinesChange) {
          onPrintingMachinesChange();
        }
      },
      400: (data) => {
        setUpdatingId(null);
        setErrorMessage(data?.error || translate('failed_to_update_printing_machine'));
      },
      404: () => {
        setUpdatingId(null);
        setErrorMessage(translate('failed_to_update_printing_machine'));
      },
      500: () => {
        setUpdatingId(null);
        setErrorMessage(translate('server_error'));
      }
    });
  };

  const renderErrorMessage = () => {
    if (!errorMessage) {
      return null;
    }
    return (
      <div style={{ color: '#dc3545', marginBottom: '10px', padding: '8px', backgroundColor: '#f8d7da', borderRadius: '4px' }}>
        {errorMessage}
      </div>
    );
  };

  const addedMachines = machines.filter((machine) => machine.widget_enabled);
  const addableMachines = machines.filter((machine) => machine.supported_by_widget_materials && !machine.widget_enabled);

  const noAddableMachinesMessage = () => {
    if (!widgetMaterials) { return translate('no_widget_materials_for_printing'); }
    if (machines.length === 0) { return translate('no_printing_machines'); }
    return translate('no_printing_machines_for_materials');
  };

  // The settings list's machine row: the name and its property pills, the action on the right.
  const machineRow = (machine, action) => (
    <tr key={machine.id} data-testid="widget-printing-machine">
      <td>
        <strong>{machine.name}</strong>
        {machine.widget_enabled && !machine.supported_by_widget_materials && (
          <div data-testid="widget-printing-machine-not-used" style={{ color: '#dc3545', fontSize: '12px' }}>
            {translate('printing_machine_not_used')}
          </div>
        )}
        <AttributePropertyTags
          properties={machine.properties}
          listingProperties={tabConfiguration?.listing_properties}
          subFormAttributes={machineFormConfiguration?.create_sub_form?.attributes}
          sectionAttributes={settingsConfiguration?.attributes}
        />
      </td>
      <td>
        <div className="float-right" style={{ whiteSpace: 'nowrap' }}>{action}</div>
      </td>
    </tr>
  );

  const machinesTable = (title, rows) => (
    <table className="table table-striped table-bordered-outside" style={{ marginTop: '10px' }}>
      <tbody>
        <tr>
          <th colSpan="2">
            <div className="float-left">{title}</div>
          </th>
        </tr>
        {rows}
      </tbody>
    </table>
  );

  const renderAddedMachines = () => {
    if (addedMachines.length === 0) {
      return (
        <div style={{ color: '#666', fontStyle: 'italic', marginBottom: '10px' }}>
          {translate('no_printing_machines_enabled')}
        </div>
      );
    }

    return machinesTable(
      `${tabConfiguration ? t(tabConfiguration.label) : t('quote_builder.printing_machine')} (${addedMachines.length})`,
      addedMachines.map((machine) => machineRow(machine, (
        <a role="button" style={{ cursor: 'pointer' }} onClick={() => updateWidgetStatus(machine, false)}>
          <i className="fa fa-trash-o" aria-hidden="true"></i>{' '}
          {updatingId === machine.id ? translate('removing') : translate('remove')}
        </a>
      )))
    );
  };

  const renderAddPopupContent = () => (
    <div style={{ padding: '20px', minWidth: '500px' }}>
      <h4>{translate('add_printing_machine')}</h4>
      {renderErrorMessage()}
      {addableMachines.length === 0 ? (
        <p>{noAddableMachinesMessage()}</p>
      ) : (
        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {machinesTable(
            translate('select_printing_machine'),
            addableMachines.map((machine) => machineRow(machine, (
              <a role="button" style={{ cursor: 'pointer' }} onClick={() => updateWidgetStatus(machine, true)}>
                <i className="fa fa-plus-square-o" aria-hidden="true"></i>{' '}
                {translate('add_to_widget')}
              </a>
            )))
          )}
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div style={{ padding: '10px', color: '#666' }}>
        {translate('loading')}
      </div>
    );
  }

  return (
    <div className={styles.fieldGroup}>
      <p style={{ color: '#666' }}>{translate('printing_machines_information')}</p>
      {!showAddPopup && renderErrorMessage()}
      {renderAddedMachines()}
      <button type="button" className="btn simple-btn" onClick={() => setShowAddPopup(true)}>
        <i className="fa fa-plus-square-o" aria-hidden="true"></i>{' '}
        {translate('add_printing_machine')}
      </button>
      {showAddPopup && (
        <ContentPopup
          myRef={addPopupRef}
          autoOpen={true}
          content={renderAddPopupContent()}
          cancelButton={false}
          beforeClose={() => {
            setShowAddPopup(false);
            setErrorMessage('');
          }}
        />
      )}
    </div>
  );
};

export default PrintingMachinesSection;
